// src/pages/Admin/Team/CreateTeamMemberModal.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaSave,
  FaTimes,
  FaImage,
  FaEnvelope,
  FaPhone,
  FaUser,
  FaUserTie,
  FaListOl,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { addTeamMember } from "../../../services/teamServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Quill toolbar/modules (présentation seulement)
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const CreateTeamMemberModal = ({ isActive, onClose, refreshTeam }) => {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    fonction: "",
    photoURL: "",
    categorie: "",
    position: "",
    mail_member: "",
    tel_member: "",
    bio_member: "",
  });

  const defaultAvatar = "/img/user_avatar.jpg";
  const [previewSrc, setPreviewSrc] = useState(defaultAvatar);
  const dialogRef = useRef(null);

  // Gestion ouverture/fermeture : lock scroll + focus + ESC
  useEffect(() => {
    if (!isActive) return;

    // Préview à l'ouverture
    setPreviewSrc(formData.photoURL?.trim() ? formData.photoURL : defaultAvatar);

    // Lock scroll
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    // Focus premier champ
    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    // ESC pour fermer
    const onEsc = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onEsc);

    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const resetForm = () => {
    setFormData({
      nom: "",
      prenom: "",
      fonction: "",
      photoURL: "",
      categorie: "",
      position: "",
      mail_member: "",
      tel_member: "",
      bio_member: "",
    });
    setPreviewSrc(defaultAvatar);
  };

  const handleClose = () => {
    onClose?.();
    resetForm();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "photoURL") {
      const val = value.trim();
      setPreviewSrc(val ? val : defaultAvatar);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        photoURL: formData.photoURL.trim() !== "" ? formData.photoURL : defaultAvatar,
      };
      await addTeamMember(dataToSend);
      toast.success("Membre ajouté avec succès");
      handleClose();
      refreshTeam?.();
    } catch (error) {
      console.error("Erreur lors de l'ajout du membre", error);
      toast.error("Erreur lors de l'ajout du membre");
    }
  };

  if (!isActive) return null;

  // --- PORTAL ---
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Créer un membre de l'équipe"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh] sm:max-h-[92dvh]"
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Ajouter un Nouveau Membre
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Renseignez les informations du membre de l’équipe.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps scrollable + Footer sticky dans le même <form> */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: "touch" }}>
          {/* Rangée identité */}
          <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-4 sm:gap-6">
            {/* Avatar + URL */}
            <div className="flex md:flex-col gap-4 md:gap-5">
              <div className="relative self-start">
                <img
                  src={previewSrc}
                  alt="Aperçu"
                  onError={(e) => {
                    e.currentTarget.src = defaultAvatar;
                  }}
                  className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover shadow ring-2 ring-white dark:ring-slate-800"
                />
                <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                  <FaImage className="text-[12px]" />
                </span>
              </div>

              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  URL de la photo
                </label>
                <div className="mt-1 relative">
                  <input
                    type="url"
                    name="photoURL"
                    value={formData.photoURL}
                    onChange={handleChange}
                    placeholder="https://…/photo.jpg (facultatif)"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    inputMode="url"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  PNG/JPG/WebP recommandé.
                  <br />Taille carrée conseillée (512×512).
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Si vous n'avez pas de lien URL de la photo,
                  <br />
                  laissez vide (avatar par défaut).
                  <br />Vous pourrez la changer plus tard.
                </p>
              </div>
            </div>

            {/* Champs identité */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Nom <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaUser />
                  </span>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    placeholder="DUPONT"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Prénom <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaUser />
                  </span>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    placeholder="Alice"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Fonction <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaUserTie />
                  </span>
                  <input
                    type="text"
                    name="fonction"
                    value={formData.fonction}
                    onChange={handleChange}
                    required
                    placeholder="Enseignant, Trésorier, Président…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Catégorie <span className="text-pink-600">*</span>
                </label>
                <input
                  type="text"
                  name="categorie"
                  value={formData.categorie}
                  onChange={handleChange}
                  required
                  placeholder="Bureau, Enseignants, Bénévoles…"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Position (ordre) <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaListOl />
                  </span>
                  <input
                    type="number"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    min="0"
                    step="1"
                    placeholder="0"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Email <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaEnvelope />
                  </span>
                  <input
                    type="email"
                    name="mail_member"
                    value={formData.mail_member}
                    onChange={handleChange}
                    required
                    placeholder="exemple@club.fr"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    inputMode="email"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Téléphone <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaPhone />
                  </span>
                  <input
                    type="tel"
                    name="tel_member"
                    value={formData.tel_member}
                    onChange={handleChange}
                    required
                    placeholder="+33 6 12 34 56 78"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Biographie
                </label>
                <div className="mt-1 rounded-xl border border-slate-300 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 quill-wrapper">
                  <ReactQuill
                    value={formData.bio_member}
                    onChange={(v) => setFormData({ ...formData, bio_member: v })}
                    placeholder="Parcours, rôles dans le club, spécialités, disponibilités…"
                    modules={modules}
                    formats={formats}
                    className="min-h-[180px]"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Vous pouvez insérer du texte enrichi, des listes, des liens et des images.
                </p>
              </div>
            </div>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-6">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <Button
                text="Annuler"
                icon={FaTimes}
                onClick={handleClose}
                type="button"
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
              />
              <Button text="Enregistrer" icon={FaSave} type="submit" className="!rounded-xl gap-2" />
            </div>
          </div>
        </form>
      </div>

      {/* Styles locaux */}
      <style>{`
        /* Placeholders */
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        /* Scrollbar esthétique pour le corps de modale */
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb { background: rgba(148,163,184,.35); }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }

        /* Quill dark-mode */
        .dark .quill-wrapper .ql-toolbar.ql-snow { background: rgba(15,23,42,.98); border-color: #334155; }
        .dark .quill-wrapper .ql-container.ql-snow { border-color: #334155; background: rgba(2,6,23,.7); }
        .dark .quill-wrapper .ql-editor { color: #e2e8f0; }
        .dark .ql-snow .ql-stroke { stroke: #e2e8f0 !important; }
        .dark .ql-snow .ql-fill { fill: #e2e8f0 !important; }
        .dark .ql-snow .ql-picker,
        .dark .ql-snow .ql-picker-label,
        .dark .ql-snow .ql-picker-options,
        .dark .ql-snow .ql-toolbar button { color: #e2e8f0 !important; }
        .dark .ql-snow .ql-picker-options { background: #0b1220; border-color: #334155; }
        .dark .ql-snow .ql-toolbar button:hover .ql-stroke { stroke: #fff !important; }
        .dark .ql-snow .ql-toolbar button:hover .ql-fill { fill: #fff !important; }

        /* Placeholder Quill */
        .quill-wrapper .ql-editor.ql-blank::before { color: #64748b; opacity: .8; }
        .dark .quill-wrapper .ql-editor.ql-blank::before { color: #94a3b8; opacity: .7; }
      `}</style>
    </div>,
    document.body
  );
};

export default CreateTeamMemberModal;
